import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Modal,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import EnergySavingsLeafIcon from "@mui/icons-material/EnergySavingsLeaf";
import BuildIcon from "@mui/icons-material/Build";
import CarRepairIcon from "@mui/icons-material/CarRepair";
import BoltIcon from "@mui/icons-material/Bolt";
import SchoolIcon from "@mui/icons-material/School";
import SearchIcon from "@mui/icons-material/Search";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import KendoButton from "assets/theme/components/button/kendo";
import { modalContainerStyle } from "pages/components/common/Styles";
import { setMyAppMode } from "api/users.api";
import { getUser } from "redux-store/auth/actions";
import {
  APP_MODES,
  SYSTEM_DISPLAY_NAMES,
  getDefaultRoute,
  needsDefaultSystemPick,
} from "config/appMode";

// Maps an OrganizationSystem name (the strings stored in
// OrganizationSystems.name on the server) to its (appMode, enabled) pair.
//
// Adding a new system is two lines: add the name to OrganizationSystems
// in the DB, add a row here. No schema change, no resolver change.
const SYSTEM_MAP = {
  Environmental: {
    appMode: APP_MODES.AFMS,
    enabled: true,
    icon: <EnergySavingsLeafIcon fontSize="small" sx={{ color: "#2ed66f" }} />,
    label: "Environmental",
  },
  "LiftConnect Pro": {
    appMode: APP_MODES.LCP,
    enabled: true,
    // Closest Material Icon to "car on a 2-post hoist" — a car with a
    // wrench underneath. Red to match the LiftConnect Pro brand accent.
    icon: <CarRepairIcon fontSize="small" sx={{ color: "#d32f2f" }} />,
    label: "LiftConnect Pro",
  },
  "Fixed Operations": {
    appMode: APP_MODES.FLEX2,
    enabled: true,
    // Wrench in industrial blue to match the FixedOpsPortal sidebar accent.
    icon: <BuildIcon fontSize="small" sx={{ color: "#1976d2" }} />,
    label: "Fixed Operations",
  },
  // Energy Management vertical — orange lightning bolt to match the
  // EnergyPortal sidebar accent color. Routes the user into the
  // /energy/* lane backed by energy-api on port 5079.
  Energy: {
    appMode: APP_MODES.ENERGY,
    enabled: true,
    icon: <BoltIcon fontSize="small" sx={{ color: "#ff9100" }} />,
    label: "Energy",
  },
  // Asset training qualification. Violet to match the TrainingPortal rail
  // accent (#7c3aed) — keep the two hexes identical or the icon here stops
  // reading as the same product as that sidebar.
  Training: {
    appMode: APP_MODES.TRAINING,
    enabled: true,
    icon: <SchoolIcon fontSize="small" sx={{ color: "#7c3aed" }} />,
    label: "Training",
  },
  // Generic asset-class inspections. Deep cyan to match the InspectionsPortal
  // rail accent (#0e7490) — keep the two hexes identical or the icon here
  // stops reading as the same product as that sidebar.
  Inspections: {
    appMode: APP_MODES.INSPECTIONS,
    enabled: true,
    icon: <SearchIcon fontSize="small" sx={{ color: "#0e7490" }} />,
    label: "Inspections",
  },
};

// Build the list the picker actually renders. Each entry is a row in the
// modal. `selectValue` is what gets passed to setMyAppMode on save (the
// app-mode string), so disabled rows still carry one for consistency.
const buildOptions = (organizationSystems) => {
  const systems = Array.isArray(organizationSystems) ? organizationSystems : [];
  return systems.map((systemName) => {
    const entry = SYSTEM_MAP[systemName];
    if (entry) {
      return {
        systemName,
        label: entry.label,
        selectValue: entry.appMode,
        enabled: entry.enabled,
        icon: entry.icon,
        hint: entry.hint,
      };
    }
    // Unknown system — render it but mark as not-yet-supported so users
    // see what their org has even before we wire a mode for it.
    return {
      systemName,
      label: SYSTEM_DISPLAY_NAMES[systemName] || systemName,
      selectValue: systemName,
      enabled: false,
      icon: null,
      hint: "Coming soon",
    };
  });
};

/**
 * "Select your default system" modal — two open paths:
 *
 *   1. **Auto** (first login). Self-gates against needsDefaultSystemPick.
 *      Mounted unconditionally by App.js; renders null when the gate is
 *      false (backoffice, single-system, or user already picked).
 *      Non-dismissable: blocks the post-login landing flow until the user
 *      picks.
 *
 *   2. **Manual** (`forceOpen` prop). Opens on demand when the user clicks
 *      the Flex2 logo on either sidebar. Dismissable via a Close button.
 *      Calls `onClose` after save or close.
 *
 * Option list is driven by `userDetails.organizationSystems` (the org's
 * mapped systems), not by app-mode keys. Disabled rows render greyed-out
 * with a "Coming soon" hint.
 */
function DefaultSystemPicker({ forceOpen = false, onClose }) {
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useSelector((state) => state.auth.user);
  const userDetails = user?.userDetails;

  const options = useMemo(
    () => buildOptions(userDetails?.organizationSystems),
    [userDetails?.organizationSystems]
  );
  const enabledOptions = useMemo(() => options.filter((o) => o.enabled), [options]);

  // Auto-open whenever the first-login gate says so OR the caller forces
  // the picker open via the logo-click path.
  //
  // The first-login gate keys on availableAppModes (role-derived), but the
  // rows we render come from organizationSystems (the org's DB-mapped
  // systems) run through SYSTEM_MAP. Those two server fields can disagree:
  // a vendor org reports availableAppModes ['flex2','afms'] (length 2 → gate
  // fires) while organizationSystems is [] (zero rows → zero selectable
  // options). That mismatch otherwise renders a *blocking* modal with
  // nothing to pick (Save disabled) and no Close button — a hard lock. Only
  // auto-open (blocking) when there is at least one SELECTABLE system; with
  // none, there is nothing to decide, so let the user land via their
  // server-resolved effectiveAppMode instead of trapping them.
  //
  // Never on the sign-in page itself (2026-10-27): its later steps — the 2FA code and the
  // first-login password change — come after the token, so the gate fired over them, and
  // Save's navigate() unmounted the password change, which signs the user out as it goes.
  // The picker opens once they land.
  const onSignIn = /^\/login(\/|$)/.test(pathname || "");
  const autoOpen = !onSignIn && pickerWillAutoOpen(userDetails);
  const isOpen = autoOpen || forceOpen;

  // Default the selection to the currently-effective system if it's
  // enabled; otherwise the first enabled option; otherwise empty.
  const initialSelection = useMemo(() => {
    const current = userDetails?.effectiveAppMode;
    const currentEnabled = enabledOptions.find((o) => o.selectValue === current);
    if (currentEnabled) return current;
    return enabledOptions[0]?.selectValue ?? "";
  }, [userDetails?.effectiveAppMode, enabledOptions]);

  const [selected, setSelected] = useState(initialSelection);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Refresh the default selection if the underlying options change while
  // the picker is open (rare — but happens on userDetails reload).
  useEffect(() => {
    if (!isOpen) return;
    setSelected(initialSelection);
    setError(null);
  }, [isOpen, initialSelection]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (saving) return;
    if (onClose) onClose();
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await setMyAppMode(selected);
      // Re-fetch userDetails so preferredAppMode + effectiveAppMode
      // refresh. needsDefaultSystemPick then flips false (first-login
      // path) and the App.js-level sidebar swap takes effect.
      await dispatch(getUser());
      const label =
        SYSTEM_DISPLAY_NAMES[selected] ||
        options.find((o) => o.selectValue === selected)?.label ||
        selected;
      toast.success(`Default system set to ${label}`);
      // Read FRESH userDetails from the store (closure `userDetails` is
      // pre-getUser and would route off stale flags). Force the picked
      // mode so the navigation picks the right home for the new system,
      // even before any subsequent re-render reflects the resolver's
      // server-side effectiveAppMode.
      const fresh = store.getState()?.auth?.user?.userDetails || userDetails || {};
      navigate(getDefaultRoute({ ...fresh, effectiveAppMode: selected }));
      if (onClose) onClose();
    } catch (err) {
      console.error("Failed to save default system:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Couldn't save your selection. Try again.";
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      // Only the manual (forceOpen) path is dismissable via backdrop —
      // first-login must stay blocking.
      onClose={forceOpen ? handleClose : undefined}
      aria-labelledby="default-system-picker-title"
      aria-describedby="default-system-picker-description"
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <SoftBox sx={modalContainerStyle}>
        <SoftTypography
          id="default-system-picker-title"
          variant="h4"
          component="h2"
          gutterBottom
          mb={1}
        >
          {forceOpen ? "Switch system" : "Select your default system"}
        </SoftTypography>
        <SoftTypography
          id="default-system-picker-description"
          variant="body2"
          fontWeight="regular"
          color="text"
          mb={2}
        >
          {forceOpen
            ? "Pick the system you want to open. Disabled options are coming soon."
            : "Your organization has access to more than one system. Pick the one you want to open by default. You can change this later by clicking the Flex2 logo."}
        </SoftTypography>

        <FormControl component="fieldset" fullWidth error={Boolean(error)}>
          <RadioGroup
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            aria-label="Default system"
          >
            {options.map((opt) => (
              <FormControlLabel
                key={opt.systemName}
                value={opt.selectValue}
                disabled={saving || !opt.enabled}
                control={<Radio />}
                sx={{ opacity: opt.enabled ? 1 : 0.5 }}
                label={
                  <SoftBox
                    component="span"
                    sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
                  >
                    {opt.icon || null}
                    <SoftTypography variant="button" fontWeight="medium">
                      {opt.label}
                    </SoftTypography>
                    {opt.hint ? (
                      <SoftTypography
                        variant="caption"
                        color="text"
                        sx={{ opacity: 0.7, ml: 0.5 }}
                      >
                        ({opt.hint})
                      </SoftTypography>
                    ) : null}
                  </SoftBox>
                }
              />
            ))}
          </RadioGroup>
          {error ? <FormHelperText>{error}</FormHelperText> : null}
          {enabledOptions.length === 0 ? (
            <FormHelperText>
              No selectable systems are available for your organization yet.
            </FormHelperText>
          ) : null}
        </FormControl>

        <SoftBox
          sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}
        >
          {forceOpen ? (
            <SoftButton onClick={handleClose} color="dark" disabled={saving}>
              Close
            </SoftButton>
          ) : null}
          <SoftBox sx={{ minWidth: 180 }}>
            <KendoButton
              type="button"
              label={saving ? "Saving..." : "Save and continue"}
              onClick={handleSave}
              loading={saving}
              disabled={saving || !selected || enabledOptions.length === 0}
              color="info"
              fullWidth
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            />
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </Modal>
  );
}

DefaultSystemPicker.propTypes = {
  forceOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

// Whether the first-login picker opens (blocking) for this user once they are past the
// sign-in page. Other first-run prompts (the quick sign-in PIN offer) wait for it.
export const pickerWillAutoOpen = (userDetails) =>
  needsDefaultSystemPick(userDetails) && buildOptions(userDetails?.organizationSystems).some((o) => o.enabled);

export default DefaultSystemPicker;
